/**
 * Constants for the CreateTicket page
 * Inlined from Thaklis app: StatFieldData, fieldCategoryMapping, fieldCategoryUtils, fieldContextUtils, fieldPatternUtils
 */

// === STAT_FIELDS (from StatFieldData.js) ===
export const DOCUMENT_TYPE_MAPPING = {
  passport_copy: 'passportCopy',
  visa_page: 'visaPage',
  photo: 'photo',
  shinasnama: 'shinasnama',
  labour_contract: 'labourContract',
  salary_certificate: 'salaryCertificate',
  birth_certificate: 'birthCertificate',
  emirates_id: 'emiratesId',
  emirates_id_copy: 'emiratesIdCopy',
  ohc: 'ohc',
  driving_license: 'drivingLicense',
  passportCopy: 'passport_copy',
  visaPage: 'visa_page',
  labourContract: 'labour_contract',
  salaryCertificate: 'salary_certificate',
  birthCertificate: 'birth_certificate',
  emiratesId: 'emirates_id',
  emiratesIdCopy: 'emirates_id_copy',
  drivingLicense: 'driving_license',
  resume: 'resume',
  policeClearanceCertificate: 'police_clearance_certificate',
  educationalCertificate: 'educational_certificate',
  experienceCertificate: 'experience_certificate'
};

export const STAT_FIELDS = {
  uid: { label: "UID", type: "TEXT" },
  firstNameEn: { label: "First Name (English)", type: "TEXT" },
  middleNameEn: { label: "Middle Name (English)", type: "TEXT" },
  lastNameEn: { label: "Last Name (English)", type: "TEXT" },
  firstNameAr: { label: "First Name (Arabic)", type: "TEXT" },
  middleNameAr: { label: "Middle Name (Arabic)", type: "TEXT" },
  lastNameAr: { label: "Last Name (Arabic)", type: "TEXT" },
  nameEn: { label: "Name (English)", type: "TEXT" },
  nameAr: { label: "Name (Arabic)", type: "TEXT" },
  dob: { label: "Date Of Birth", type: "DATE" },
  gender: { label: "Gender", type: "SELECT" },
  nationality: { label: "Nationality", type: "TEXT" },
  passportNumber: { label: "Passport Number", type: "TEXT" },
  passportIssue: { label: "Passport Issue", type: "DATE" },
  passportExpiry: { label: "Passport Expiry", type: "DATE" },
  passportIssuePlaceEn: { label: "Passport Issue Place (English)", type: "TEXT" },
  passportIssuePlaceAr: { label: "Passport Issue Place (Arabic)", type: "TEXT" },
  emiratesId: { label: "Emirates Id", type: "TEXT" },
  emiratesIdIssueDate: { label: "Emirates Id Issue Date", type: "DATE" },
  emiratesIdExpiryDate: { label: "Emirates Id Expiry Date", type: "DATE" },
  emiratesIdCopy: { label: "Emirates Id Copy", type: "FILE" },
  birthPlaceEn: { label: "Birth Place (English)", type: "TEXT" },
  birthPlaceAr: { label: "Birth Place (Arabic)", type: "TEXT" },
  birthCertificate: { label: "Birth Certificate", type: "FILE" },
  emirate: { label: "Emirate", type: "TEXT" },
  address: { label: "Address", type: "TEXT" },
  area: { label: "Area", type: "TEXT" },
  buildingNumber: { label: "Building Number", type: "TEXT" },
  poBox: { label: "PO Box", type: "TEXT" },
  mobileNumber: { label: "Mobile Number", type: "PHONE" },
  landNumber: { label: "Land Number", type: "PHONE" },
  deliveryNumber: { label: "Delivery Number", type: "PHONE" },
  email: { label: "Email", type: "EMAIL" },
  fileNumber: { label: "File Number", type: "TEXT" },
  passportCopy: { label: "Passport Copy", type: "FILE" },
  visaPage: { label: "Visa Page", type: "FILE" },
  photo: { label: "Photo", type: "FILE" },
  shinasnama: { label: "Shinsnama", type: "FILE" },
  labourContract: { label: "Labour Contract", type: "FILE" },
  salaryCertificate: { label: "Salary Certificate", type: "FILE" },
  ohc: { label: "OHC", type: "FILE" },
  drivingLicense: { label: "Driving License", type: "FILE" },
  biometricLocation: { label: "Biometric Location", type: "TEXT" },
  medicalLocation: { label: "Medical Location", type: "SELECT" },
  ohhLocation: { label: "Ohh Location", type: "TEXT" },
  contactPerson: { label: "Contact Person", type: "PERSON" }
};

export const MEDICAL_LOCATIONS = [
  "Al Nahda Occupational Health Center",
  "Salem Abu Hail",
  "Salem, Al Muhaisnah Branch",
  "Salem ENOC",
  "Salem Center – JVC – Circle Mall",
  "Al Yalayis Fitness Medical Center - Jabal Ali Branch",
  "Al Muhaisnah Medical Fitness Center",
  "Zabeel Health Center (for Zabeel Residents)",
  "Al Lusaily Health Center",
  "Hatta Hospital",
  "Al Rashidiya Medical Fitness Center",
  "Al Yalayis Medical Fitness Center",
  "Dubai Airport Center",
  "Al Quoz Mall Medical Fitness Center",
  "Bur Dubai Medical Fitness Center",
  "Al Karama Medical Fitness Center",
  "Al Garhoud Medical Fitness Center",
  "Smart Salem – DIFC, Index Tower",
  "Smart Salem – TECOM, Dubai Knowledge Park",
  "Smart Salem – Al Wasl, City Walk"
];

// === FIELD_CATEGORIES (from fieldCategoryUtils.js) ===
export const FIELD_CATEGORIES = {
  PERSONAL_INFO: 'PERSONAL_INFO',
  IDENTIFICATION: 'IDENTIFICATION',
  CONTACT_INFO: 'CONTACT_INFO',
  ADDRESS: 'ADDRESS',
  DOCUMENTS: 'DOCUMENTS',
  FINANCIAL: 'FINANCIAL',
  EMPLOYMENT: 'EMPLOYMENT',
  MEDICAL: 'MEDICAL',
  EDUCATION: 'EDUCATION',
  SERVICE_SPECIFIC: 'SERVICE_SPECIFIC',
  OTHER: 'OTHER',
  EMPLOYMENT_INFO: 'EMPLOYMENT_INFO',
  ASSIGNMENT: 'ASSIGNMENT',
  STATUS: 'STATUS',
  LOCATIONS: 'LOCATIONS',
  GENERAL: 'GENERAL'
};

export const CATEGORY_DISPLAY_NAMES = {
  'PERSONAL_INFO': 'Personal Information',
  'IDENTIFICATION': 'Identification Documents',
  'CONTACT_INFO': 'Contact Information',
  'ADDRESS': 'Address Details',
  'DOCUMENTS': 'Documents & Files',
  'LOCATIONS': 'Location Information',
  'EMPLOYMENT': 'Employment Details',
  'SERVICE_SPECIFIC': 'Service Specific Fields',
  'FINANCIAL': 'Financial Information',
  'LEGAL': 'Legal Information',
  'MEDICAL': 'Medical Information',
  'EDUCATION': 'Education Information',
  'OTHER': 'Other Information',
  'EMPLOYMENT_INFO': 'Employment Information',
  'ASSIGNMENT': 'Assignment Details',
  'STATUS': 'Status Information'
};

// === CONTEXT_TYPES (from fieldContextUtils.js) ===
export const CONTEXT_TYPES = {
  DEFAULT: 'DEFAULT',
  EMPLOYEE_SELECTED: 'EMPLOYEE_SELECTED'
};

export const SECTION_TYPES = {
  CUSTOMER_INFO: 'CUSTOMER_INFO',
  DOCUMENTS: 'DOCUMENTS'
};

// === STATIC_FIELD_CATEGORY_MAP (from fieldCategoryMapping.js) ===
export const STATIC_FIELD_CATEGORY_MAP = {
  firstNameEn: FIELD_CATEGORIES.PERSONAL_INFO,
  middleNameEn: FIELD_CATEGORIES.PERSONAL_INFO,
  lastNameEn: FIELD_CATEGORIES.PERSONAL_INFO,
  firstNameAr: FIELD_CATEGORIES.PERSONAL_INFO,
  middleNameAr: FIELD_CATEGORIES.PERSONAL_INFO,
  lastNameAr: FIELD_CATEGORIES.PERSONAL_INFO,
  nameEn: FIELD_CATEGORIES.PERSONAL_INFO,
  nameAr: FIELD_CATEGORIES.PERSONAL_INFO,
  dob: FIELD_CATEGORIES.PERSONAL_INFO,
  gender: FIELD_CATEGORIES.PERSONAL_INFO,
  nationality: FIELD_CATEGORIES.PERSONAL_INFO,
  passportNumber: FIELD_CATEGORIES.PERSONAL_INFO,
  passportIssue: FIELD_CATEGORIES.PERSONAL_INFO,
  passportExpiry: FIELD_CATEGORIES.PERSONAL_INFO,
  passportIssuePlaceEn: FIELD_CATEGORIES.PERSONAL_INFO,
  passportIssuePlaceAr: FIELD_CATEGORIES.PERSONAL_INFO,
  birthPlaceEn: FIELD_CATEGORIES.PERSONAL_INFO,
  birthPlaceAr: FIELD_CATEGORIES.PERSONAL_INFO,
  birthCertificate: FIELD_CATEGORIES.PERSONAL_INFO,
  area: FIELD_CATEGORIES.PERSONAL_INFO,
  emiratesId: FIELD_CATEGORIES.IDENTIFICATION,
  emiratesIdIssueDate: FIELD_CATEGORIES.IDENTIFICATION,
  emiratesIdExpiryDate: FIELD_CATEGORIES.IDENTIFICATION,
  mobileNumber: FIELD_CATEGORIES.CONTACT_INFO,
  email: FIELD_CATEGORIES.CONTACT_INFO,
  address: FIELD_CATEGORIES.ADDRESS,
  poBox: FIELD_CATEGORIES.ADDRESS,
  passportCopy: FIELD_CATEGORIES.DOCUMENTS,
  visaPage: FIELD_CATEGORIES.DOCUMENTS,
  labourContract: FIELD_CATEGORIES.DOCUMENTS,
  salaryCertificate: FIELD_CATEGORIES.DOCUMENTS,
  photo: FIELD_CATEGORIES.DOCUMENTS,
  shinasnama: FIELD_CATEGORIES.DOCUMENTS,
  fileNumber: FIELD_CATEGORIES.DOCUMENTS,
  emirate: FIELD_CATEGORIES.SERVICE_SPECIFIC,
  buildingNumber: FIELD_CATEGORIES.SERVICE_SPECIFIC,
  landNumber: FIELD_CATEGORIES.SERVICE_SPECIFIC,
  deliveryNumber: FIELD_CATEGORIES.SERVICE_SPECIFIC,
  biometricLocation: FIELD_CATEGORIES.LOCATIONS,
  medicalLocation: FIELD_CATEGORIES.LOCATIONS,
  ohhLocation: FIELD_CATEGORIES.LOCATIONS,
};

// === FIELD PATTERNS (from fieldPatternUtils.js) ===
export const DOCUMENT_FIELD_PATTERNS = [
  'file', 'document', 'certificate', 'upload', 'photo',
  'signature', 'copy', 'scan', 'passport', 'id', 'contract', 'visa', 'shinasnama', 'labour'
];

export const SPECIFIC_DOCUMENT_FIELDS = [
  'birthCertificate', 'passportCopy', 'visaPage', 'salaryCertificate',
  'photo', 'shinasnama', 'labourContract'
];

export const STATIC_DOCUMENT_FIELDS = [
  'birthCertificate', 'passportCopy', 'visaPage', 'salaryCertificate',
  'photo', 'shinasnama', 'labourContract'
];

export const DOCUMENT_FIELDS_LIST = [
  'passportCopy', 'visaPage', 'birthCertificate', 'salaryCertificate',
  'labourContract', 'emiratesId', 'photo', 'shinasnama'
];

// Fields that hold several titled certificate instances (each with multiple files),
// rendered via TicketMultiCertificateField.
export const MULTI_INSTANCE_CERTIFICATE_FIELDS = ['educationalCertificate', 'experienceCertificate'];

// Full list of static document fields the ticket submit / write-back logic must handle
// (single-instance uploads + multi-instance certificates). Mirrors Thaklis NewTicket.
export const SUBMIT_DOCUMENT_FIELDS = [
  'passportCopy', 'visaPage', 'birthCertificate', 'salaryCertificate', 'labourContract',
  'emiratesId', 'photo', 'resume', 'policeClearanceCertificate',
  'educationalCertificate', 'experienceCertificate'
];

// Static document fields that live only in CustomerStatFieldInfo (not the categorized grid).
// Mirrors Thaklis getStaticDocumentFields().
export const getStaticDocumentFields = () => [
  'birthCertificate', 'passportCopy', 'visaPage', 'salaryCertificate', 'photo',
  'shinasnama', 'labourContract', 'resume', 'policeClearanceCertificate',
  'educationalCertificate', 'experienceCertificate'
];
